import { AxiosInstance } from 'axios'
import {
    AuthApi,
    DEVICE_ID_STORAGE_KEY,
    DEVICE_TOKEN_STORAGE_KEY,
    DeviceLoginRequest,
    LoginRequest,
    ResetPasswordChangeRequest,
    ResetPasswordRequest
} from 'src/auth/AuthApi'
import { StringStorage } from 'src/lib/StringStorage'
import { ClientUser } from 'src/api/defs/ClientUser.type'
import { Store } from '@core/types'
import { take } from 'rxjs/operators'
import { RegisterUserCmd } from 'src/api/defs/RegisterUserCmd.type'
import { Observable, Subject } from 'rxjs'
import { RegistrationResponse } from 'src/api/defs/RegistrationResponse.type'

export class UserService {
    private readonly api: AuthApi

    private readonly loginSink: Subject<ClientUser>
    private readonly loginTimeoutMs = 15000
    private readonly loginIncompleteErrorCode = 'auth/login-incomplete'

    constructor(
        private http: AxiosInstance,
        private tokenStorage: StringStorage,
        private userStore: Store<ClientUser | null>
    ) {
        this.api = new AuthApi(http)
        this.loginSink = new Subject()
    }

    // Promise interface to pub-sub auth interface to be used from UI
    logIn = async (req: LoginRequest): Promise<any> => {
        const loginResponse = await this.api.logIn(req)

        if (loginResponse.deviceToken) {
            localStorage.setItem('deviceToken', loginResponse.deviceToken)
        }

        await this.longInWithToken(loginResponse.token)
    }

    revokeDeviceToken = async (req: DeviceLoginRequest): Promise<void> => {
        await this.api.revokeDeviceToken(req)

        localStorage.removeItem(DEVICE_TOKEN_STORAGE_KEY)
        localStorage.removeItem(DEVICE_ID_STORAGE_KEY)
    }

    logInWithDeviceToken = async (req: DeviceLoginRequest): Promise<any> => {
        const token = await this.api.deviceLogIn(req)

        await this.longInWithToken(token)
    }

    longInWithToken = (token: string): Promise<any> =>
        new Promise((res, rej) => {
            let isSettled = false
            let timeoutId: ReturnType<typeof setTimeout> | null = null

            const resolveOnce = (user: ClientUser) => {
                if (isSettled) return
                isSettled = true
                if (timeoutId) clearTimeout(timeoutId)
                this.loginSink.next(user)
                res(user)
            }

            const rejectOnce = () => {
                if (isSettled) return
                isSettled = true
                if (timeoutId) clearTimeout(timeoutId)
                this.tokenStorage.clear()
                const error = new Error(
                    this.loginIncompleteErrorCode
                ) as Error & { code: string }
                error.code = this.loginIncompleteErrorCode
                rej(error)
            }

            this.tokenStorage.change(token)
            // Wait for userStore to report loaded user after token was stored.
            // If it never arrives (failed /current call), fail fast instead of endless loader.
            timeoutId = setTimeout(() => {
                console.error(
                    'Login timeout: user profile was not loaded after token was set'
                )
                rejectOnce()
            }, this.loginTimeoutMs)

            this.userStore
                .changes()
                .pipe(take(1))
                .subscribe(u => {
                    if (isSettled) return
                    if (u) {
                        resolveOnce(u)
                        return
                    }

                    console.error(
                        'Login failed: token was set but current user is null'
                    )
                    rejectOnce()
                })
        })

    $login = (): Observable<ClientUser> => {
        return this.loginSink
    }

    register = async (req: RegisterUserCmd): Promise<RegistrationResponse> =>
        this.api.register(req)

    passwordResetRequest = async (req: ResetPasswordRequest): Promise<void> =>
        this.api.passwordResetRequest(req)

    passwordResetChange = async (
        req: ResetPasswordChangeRequest
    ): Promise<void> => this.api.passwordResetChange(req)

    logOut = async () => {
        this.tokenStorage.clear()

        const deviceId = localStorage.getItem(DEVICE_ID_STORAGE_KEY)
        const deviceToken = localStorage.getItem(DEVICE_TOKEN_STORAGE_KEY)

        if (deviceId && deviceToken) {
            await this.revokeDeviceToken({
                deviceId,
                deviceToken
            })
        }
    }
}
